import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, TemplateRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzModalModule, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { TrackingLane } from '../../model/iTrackingLane';
import { TrackingLaneService } from '../../services/tracking-lane/tracking-lane.service';

@Component({
  selector: 'tracking-lanes',
  standalone: true,
  imports: [ CommonModule, FormsModule, NzButtonModule, NzIconModule, NzTableModule, NzFormModule,
    NzInputModule, NzModalModule ],
  templateUrl: './tracking-lanes.component.html',
  styleUrl: './tracking-lanes.component.scss'
})
export class TrackingLanesComponent implements OnInit {
  private router = inject(Router);
  private trackingLaneService = inject(TrackingLaneService);
  private modal = inject(NzModalService);

  trackingLanes: TrackingLane[] = [];
  newLaneName = '';
  isCreating = false;
  isLoading = true;

  ngOnInit(): void {
    this.trackingLaneService.getTrackingLanes().subscribe({
      next: res => {
        this.trackingLanes = res;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  openCreateModal(tplContent: TemplateRef<{}>, tplFooter: TemplateRef<{}>): void {
    this.newLaneName = '';
    const modalRef = this.modal.create({
      nzTitle: 'Add tracking lane',
      nzContent: tplContent,
      nzFooter: tplFooter,
      nzMaskClosable: false,
      nzClosable: !this.isCreating
    });

    modalRef.afterClose.subscribe(() => {
      this.isCreating = false;
    });
  }

  createTrackingLane(modalRef: NzModalRef): void {
    if (!this.newLaneName.trim()) return;

    this.isCreating = true;
    this.trackingLaneService.newTrackingLane(this.newLaneName.trim()).subscribe({
      next: res => {
        this.isCreating = false;
        modalRef.close();
        this.router.navigateByUrl(`tracking-lane/${res.id}`);
      },
      error: () => {
        this.isCreating = false;
      }
    });
  }

  editTrackingLane(lane: TrackingLane): void {
    this.router.navigateByUrl(`tracking-lane/${lane.id}`);
  }

  deleteTrackingLane(lane: TrackingLane): void {
    this.modal.confirm({
      nzTitle: 'Delete tracking lane?',
      nzContent: 'This tracking lane will be permanently removed.',
      nzOkDanger: true,
      nzOkText: 'Delete',
      nzCancelText: 'Cancel',
      nzOnOk: () =>
        firstValueFrom(this.trackingLaneService.deleteTrackingLane(lane.id!)).then(() => {
          this.trackingLanes = this.trackingLanes.filter(l => l.id !== lane.id);
        })
    });
  }
}
